import { Link, useParams, Navigate } from "react-router-dom";
import { GUIDES, findGuide } from "@/data/guides";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import { ArrowRight, Clock3, ArrowLeft, Calendar } from "lucide-react";

export function GuidesList() {
  return (
    <>
      <SEO
        title="Guide noleggio audio, luci e DJ | Spectrum Como e Milano"
        description="Guide pratiche per organizzare al meglio il tuo evento: come scegliere l'impianto audio, quale console DJ noleggiare, illuminazione professionale."
        canonical="https://www.spectrumservice.it/guide"
      />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16">
        <div className="text-[10px] font-mono uppercase tracking-widest text-rose-500 mb-2">
          Guide & Consigli
        </div>
        <h1 className="font-heading text-3xl sm:text-5xl font-black uppercase tracking-tight text-white leading-tight">
          Guide per organizzare il tuo{" "}
          <span className="text-rose-500">evento perfetto</span>
        </h1>
        <p className="text-zinc-400 mt-3 max-w-2xl text-sm sm:text-base">
          Consigli pratici dal service Spectrum di Como e Milano su audio, DJ,
          luci e video per matrimoni, feste ed eventi aziendali.
        </p>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4">
          {GUIDES.map((g) => (
            <Link
              key={g.slug}
              to={`/guide/${g.slug}`}
              className="group bg-zinc-900/90 border border-zinc-800 rounded-xl p-5 hover:border-rose-900/60 transition"
            >
              <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">
                <Calendar size={11} /> {g.date}
                <span>·</span>
                <Clock3 size={11} /> {g.reading_time}
              </div>
              <h2 className="font-heading font-bold text-white text-lg leading-tight group-hover:text-rose-400 transition">
                {g.title}
              </h2>
              <p className="text-zinc-400 text-sm mt-2 leading-relaxed">
                {g.description}
              </p>
              <div className="mt-4 inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest text-rose-500 group-hover:gap-2 transition-all">
                Leggi <ArrowRight size={12} />
              </div>
            </Link>
          ))}
        </div>
      </div>
    </>
  );
}

export function GuideDetail() {
  const { slug } = useParams();
  const g = findGuide(slug);
  if (!g) return <Navigate to="/guide" replace />;

  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: g.title,
    description: g.description,
    datePublished: g.date,
    author: { "@type": "Organization", name: "Spectrum Service" },
    publisher: {
      "@type": "Organization",
      name: "Spectrum Service",
      logo: {
        "@type": "ImageObject",
        url: "https://www.spectrumservice.it/logo.png",
      },
    },
    mainEntityOfPage: `https://www.spectrumservice.it/guide/${g.slug}`,
    keywords: g.keywords,
  };

  return (
    <>
      <SEO
        title={`${g.title} | Guide Spectrum`}
        description={g.description}
        canonical={`https://www.spectrumservice.it/guide/${g.slug}`}
        type="article"
      />
      <Breadcrumbs
        items={[
          { label: "Home", to: "/" },
          { label: "Guide", to: "/guide" },
          { label: g.title },
        ]}
      />
      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16">
        <Link
          to="/guide"
          className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest text-zinc-500 hover:text-rose-400 mb-6 transition"
        >
          <ArrowLeft size={12} /> Tutte le guide
        </Link>
        <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-rose-500 mb-3">
          <Calendar size={11} /> {g.date}
          <span>·</span>
          <Clock3 size={11} /> {g.reading_time}
        </div>
        <h1 className="font-heading text-3xl sm:text-5xl font-black uppercase tracking-tight text-white leading-tight">
          {g.title}
        </h1>
        <p className="text-zinc-400 mt-4 text-base sm:text-lg leading-relaxed">
          {g.description}
        </p>

        <div className="mt-10 space-y-8">
          {g.body.map((section, i) => (
            <section key={i}>
              <h2 className="font-heading font-black text-white text-xl sm:text-2xl uppercase tracking-tight leading-tight mb-3">
                {section.h}
              </h2>
              <p className="text-zinc-300 text-base leading-relaxed">
                {section.p}
              </p>
            </section>
          ))}
        </div>

        <div className="mt-14 pt-10 border-t border-zinc-800">
          <div className="bg-gradient-to-br from-rose-950/40 via-zinc-900 to-zinc-950 border border-rose-900/40 rounded-2xl p-6 sm:p-8">
            <h3 className="font-heading font-black text-white text-xl uppercase leading-tight">
              Ti serve l'attrezzatura per il tuo evento?
            </h3>
            <p className="text-zinc-400 mt-2 text-sm">
              Spectrum noleggia audio, luci, video e DJ set a Como, Milano e in
              tutta la Lombardia. Preventivo online in un minuto.
            </p>
            <Link
              to="/catalogo"
              className="inline-flex items-center gap-2 mt-4 min-h-[48px] px-5 rounded-xl bg-rose-600 hover:bg-rose-500 text-white font-heading font-black uppercase tracking-wider text-sm shadow-lg shadow-rose-900/40 transition"
            >
              Vai al catalogo <ArrowRight size={16} />
            </Link>
          </div>
        </div>

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}
        />
      </article>
    </>
  );
}
